/**
 * Shopify Storefront API Service
 * 
 * This service provides GraphQL client setup and authentication for the Shopify Storefront API.
 * It handles all API communication with the Shopify store including products, collections, and cart operations.
 * Features comprehensive retry mechanisms with exponential backoff and circuit breaker patterns.
 * 
 * Updated with optimized GraphQL queries to reduce over-fetching and improve performance.
 */

import { ShopifyConfig } from '../types/shopify';
import { shopifyConfig, configUtils } from '../config/shopify';
import { debugError, debugWarn } from '../utils/debugLog';
import apiRetryService, { SHOPIFY_RETRY_CONFIG } from './apiRetryService';
import {
  PRODUCTS_LISTING_QUERY,
  PRODUCT_DETAILS_QUERY,
  PRODUCTS_FOR_CART_QUERY,
  PRODUCTS_SEARCH_QUERY,
  COLLECTIONS_OPTIMIZED_QUERY,
  CART_OPTIMIZED_QUERY,
  PRODUCT_VARIANTS_QUERY,
  OptimizedQueryType,
  selectOptimalQuery
} from './optimizedShopifyQueries';

// GraphQL queries for Shopify Storefront API
export const SHOP_DETAILS_QUERY = `
  query getShopDetails {
    shop {
      name
      primaryDomain {
        host
        url
      }
      paymentSettings {
        currencyCode
        acceptedCardBrands
        enabledPresentmentCurrencies
      }
    }
  }
`;

export const PRODUCTS_QUERY = `
  query getProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          title
          description
          handle
          productType
          tags
          availableForSale
          createdAt
          updatedAt
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 10) {
            edges {
              node {
                id
                url
                altText
                width
                height
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                selectedOptions {
                  name
                  value
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                image {
                  id
                  url
                  altText
                  width
                  height
                }
              }
            }
          }
          options {
            id
            name
            values
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query getProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      title
      description
      handle
      productType
      tags
      availableForSale
      createdAt
      updatedAt
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            id
            url
            altText
            width
            height
          }
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            availableForSale
            quantityAvailable
            selectedOptions {
              name
              value
            }
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              id
              url
              altText
              width
              height
            }
          }
        }
      }
      options {
        id
        name
        values
      }
    }
  }
`;

export const COLLECTIONS_QUERY = `
  query getCollections($first: Int!, $after: String) {
    collections(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          title
          description
          handle
          updatedAt
          image {
            id
            url
            altText
            width
            height
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const CREATE_CART_MUTATION = `
  mutation createCart($cartInput: CartInput) {
    cartCreate(input: $cartInput) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        attributes {
          key
          value
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          customer {
            id
          }
          countryCode
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const UPDATE_CART_BUYER_IDENTITY_MUTATION = `
  mutation updateCartBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          customer {
            id
          }
          countryCode
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_QUERY = `
  query cartQuery($cartId: ID!) {
    cart(id: $cartId) {
      id
      createdAt
      updatedAt
      checkoutUrl
      lines(first: 10) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                price {
                  amount
                  currencyCode
                }
                product {
                  id
                  title
                  handle
                }
                image {
                  id
                  url
                  altText
                  width
                  height
                }
              }
            }
            attributes {
              key
              value
            }
          }
        }
      }
      attributes {
        key
        value
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        totalTaxAmount {
          amount
          currencyCode
        }
        totalDutyAmount {
          amount
          currencyCode
        }
      }
      buyerIdentity {
        email
        phone
        customer {
          id
        }
        countryCode
      }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_ATTRIBUTES_UPDATE_MUTATION = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        id
        checkoutUrl
        attributes {
          key
          value
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Error handling interface
export interface ShopifyError {
  message: string;
  extensions?: {
    code?: string;
    field?: string;
  };
}

/**
 * Shopify API Client Class
 * Handles all GraphQL communication with Shopify Storefront API
 */
class ShopifyApiClient {
  private config: ShopifyConfig;
  private baseUrl: string;

  constructor(config?: Partial<ShopifyConfig>) {
    // Use centralized configuration with optional overrides
    this.config = {
      ...shopifyConfig,
      ...config
    };

    this.baseUrl = configUtils.getStorefrontApiUrl(this.config);
  }

  /**
   * Execute a GraphQL query or mutation with retry logic
   */
  private async executeGraphQL<T = any>(
    query: string,
    variables?: Record<string, any>
  ): Promise<T> {
    try {
      const headers = {
        'X-Shopify-Storefront-Access-Token': this.config.storefrontAccessToken,
        'Accept': 'application/json',
      };

      const result = await apiRetryService.graphqlWithRetry<T>(
        this.baseUrl,
        query,
        variables,
        headers,
        SHOPIFY_RETRY_CONFIG
      );

      return result;
    } catch (error) {
      debugError('Shopify API Error:', error);
      
      // Enhance error with context
      const enhancedError = Object.assign(
        new Error(
          `Shopify API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        ),
        {
          originalError: error,
          query: query.split('\n')[0].trim(), // First line for context
          variables,
        }
      );
      
      throw enhancedError;
    }
  }

  /**
   * Get shop details including name, domain, and payment settings
   */
  async getShopDetails() {
    return this.executeGraphQL(SHOP_DETAILS_QUERY);
  }

  /**
   * Get products with pagination support (LEGACY - use optimized methods)
   * @deprecated Use getProductsOptimized instead
   */
  async getProducts(first: number = 20, after?: string) {
    debugWarn('getProducts is deprecated. Use getProductsOptimized for better performance.');
    return this.executeGraphQL(PRODUCTS_QUERY, { first, after });
  }

  /**
   * Get products optimized for listing display (product grid, store page)
   * Reduces payload by ~60% compared to legacy getProducts
   */
  async getProductsOptimized(
    first: number = 20, 
    after?: string, 
    sortKey?: string, 
    reverse?: boolean
  ) {
    return this.executeGraphQL(PRODUCTS_LISTING_QUERY, { 
      first, 
      after, 
      sortKey, 
      reverse 
    });
  }

  /**
   * Get full product details for individual product pages
   */
  async getProductDetails(handle: string) {
    return this.executeGraphQL(PRODUCT_DETAILS_QUERY, { handle });
  }

  /**
   * Get minimal product data for cart operations
   */
  async getProductsForCart(ids: string[]) {
    return this.executeGraphQL(PRODUCTS_FOR_CART_QUERY, { ids });
  }

  /**
   * Search products with optimized query
   */
  async searchProducts(
    query: string,
    first: number = 20,
    after?: string,
    sortKey?: string,
    reverse?: boolean
  ) {
    return this.executeGraphQL(PRODUCTS_SEARCH_QUERY, {
      first,
      after,
      query,
      sortKey,
      reverse
    });
  }

  /**
   * Get product variants only (for variant selection)
   */
  async getProductVariants(handle: string) {
    return this.executeGraphQL(PRODUCT_VARIANTS_QUERY, { handle });
  }

  /**
   * Get a single product by handle
   */
  async getProductByHandle(handle: string) {
    return this.executeGraphQL(PRODUCT_BY_HANDLE_QUERY, { handle });
  }

  /**
   * Get collections with pagination support (LEGACY)
   * @deprecated Use getCollectionsOptimized instead
   */
  async getCollections(first: number = 20, after?: string) {
    debugWarn('getCollections is deprecated. Use getCollectionsOptimized for consistency.');
    return this.executeGraphQL(COLLECTIONS_QUERY, { first, after });
  }

  /**
   * Get collections with optimized query (same as legacy but consistent naming)
   */
  async getCollectionsOptimized(first: number = 20, after?: string) {
    return this.executeGraphQL(COLLECTIONS_OPTIMIZED_QUERY, { first, after });
  }

  /**
   * Create a new cart
   */
  async createCart(cartInput?: any) {
    return this.executeGraphQL(CREATE_CART_MUTATION, { cartInput });
  }

  /**
   * Update cart buyer identity
   */
  async updateCartBuyerIdentity(cartId: string, buyerIdentity: any) {
    return this.executeGraphQL(UPDATE_CART_BUYER_IDENTITY_MUTATION, { cartId, buyerIdentity });
  }

  /**
   * Get cart by ID (LEGACY)
   * @deprecated Use getCartOptimized instead
   */
  async getCart(cartId: string) {
    debugWarn('getCart is deprecated. Use getCartOptimized for better performance.');
    return this.executeGraphQL(CART_QUERY, { cartId });
  }

  /**
   * Get cart with optimized query (reduced payload for cart operations)
   */
  async getCartOptimized(cartId: string) {
    return this.executeGraphQL(CART_OPTIMIZED_QUERY, { cartId });
  }

  /**
   * Add lines to cart
   */
  async addToCart(cartId: string, lines: any[]) {
    return this.executeGraphQL(CART_LINES_ADD_MUTATION, { cartId, lines });
  }

  /**
   * Update cart lines
   */
  async updateCartLines(cartId: string, lines: any[]) {
    return this.executeGraphQL(CART_LINES_UPDATE_MUTATION, { cartId, lines });
  }

  /**
   * Remove lines from cart
   */
  async removeFromCart(cartId: string, lineIds: string[]) {
    return this.executeGraphQL(CART_LINES_REMOVE_MUTATION, { cartId, lineIds });
  }

  /**
   * Update cart attributes
   */
  async updateCartAttributes(cartId: string, attributes: Array<{ key: string; value: string }>) {
    return this.executeGraphQL(CART_ATTRIBUTES_UPDATE_MUTATION, { cartId, attributes });
  }

  /**
   * Test the API connection with retry logic
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.getShopDetails();
      return true;
    } catch (error) {
      debugError('Shopify API connection test failed:', error);
      return false;
    }
  }

  /**
   * Get retry service status for monitoring
   */
  getRetryStatus() {
    return apiRetryService.getCircuitBreakerStatus();
  }

  /**
   * Reset retry mechanisms (useful for testing or recovery)
   */
  resetRetryMechanisms() {
    apiRetryService.resetAllCircuitBreakers();
    apiRetryService.clearPendingRequests();
  }

  /**
   * Get optimal query type for a specific use case
   */
  getOptimalQueryType(useCase: string): OptimizedQueryType {
    return selectOptimalQuery(useCase);
  }

  /**
   * Execute optimized query based on use case
   */
  async executeOptimizedQuery(useCase: string, variables: Record<string, any> = {}) {
    const queryType = this.getOptimalQueryType(useCase);
    
    switch (queryType) {
      case 'PRODUCTS_LISTING':
        return this.getProductsOptimized(
          variables.first,
          variables.after,
          variables.sortKey,
          variables.reverse
        );
      
      case 'PRODUCT_DETAILS':
        return this.getProductDetails(variables.handle);
      
      case 'PRODUCTS_FOR_CART':
        return this.getProductsForCart(variables.ids);
      
      case 'PRODUCTS_SEARCH':
        return this.searchProducts(
          variables.query,
          variables.first,
          variables.after,
          variables.sortKey,
          variables.reverse
        );
      
      case 'COLLECTIONS_OPTIMIZED':
        return this.getCollectionsOptimized(variables.first, variables.after);
      
      case 'CART_OPTIMIZED':
        return this.getCartOptimized(variables.cartId);
      
      case 'PRODUCT_VARIANTS':
        return this.getProductVariants(variables.handle);
      
      default:
        throw new Error(`Unknown query type: ${queryType}`);
    }
  }

  /**
   * Get query performance metrics
   */
  getQueryMetrics() {
    return {
      retryStatus: this.getRetryStatus(),
      optimizedQueriesAvailable: [
        'PRODUCTS_LISTING',
        'PRODUCT_DETAILS',
        'PRODUCTS_FOR_CART',
        'PRODUCTS_SEARCH',
        'COLLECTIONS_OPTIMIZED',
        'CART_OPTIMIZED',
        'PRODUCT_VARIANTS'
      ]
    };
  }
}

// Default export - singleton instance
const shopifyApi = new ShopifyApiClient();
export default shopifyApi;

// Named exports for testing and custom configurations
export { ShopifyApiClient };
export { shopifyApi };
